import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import Config from "../../Config";

import TicketTableRow from "./TicketTableRow";
import TicketTableHeader from "./TicketTableHeader";
import TicketTablePagination from "./TicketTablePagination";

const STORAGE_KEY = "ticketTableSortConfig";
const columnVisibilityConfig = Config.TicketTableColumns || {};

const BASE_COLUMNS = [
  { label: "Ticket", key: "ticket", sortable: true, valueGetter: (row) => row.ticketid },
  {
    label: "Title",
    key: "title",
    sortable: true,
    valueGetter: (row) => {
      const title = row?.metadata?.requestType ?? row?.workflowtype ?? "";
      return typeof title === "string" ? title.replace("AARP", "") : title;
    },
  },
  { label: "Subject", key: "subject", sortable: false, valueGetter: (row) => row.subject },
  { label: "Status", key: "status", sortable: true, valueGetter: (row) => row.displayStatus },
  {
    label: "Doc Class Type",
    key: "docClass",
    sortable: true,
    valueGetter: (row) => row?.metadata?.DocClass ?? row?.docClass,
    hideIfEmpty: true,
  },
  { label: "LOB", key: "lob", sortable: true, valueGetter: (row) => row.lob },
  { label: "Created", key: "created", sortable: true, valueGetter: (row) => row.creationdate },
  {
    label: "CheckNumber",
    key: "checkNumber",
    sortable: false,
    valueGetter: (row) => row?.metadata?.CheckNumber ?? row?.checkNumber,
    hideIfEmpty: true,
  },
  {
    label: "CheckType",
    key: "checkType",
    sortable: true,
    valueGetter: (row) => row?.metadata?.CheckType ?? row?.CheckType,
    hideIfEmpty: true,
  },
  {
    label: "CheckAmount",
    key: "checkAmount",
    sortable: false,
    valueGetter: (row) => row?.metadata?.Amount ?? row?.checkAmount,
    hideIfEmpty: true,
  },
  { label: "Comments", key: "comments", sortable: false, valueGetter: (row) => row.message },
  { label: "View", key: "view", sortable: false },
  { label: "Action", key: "action", sortable: false },
];

const hasValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  return true;
};

const getColumnValue = (column, row) => (column.valueGetter ? column.valueGetter(row) : row?.[column.key]);

const applyVisibilityConfig = (column) => {
  const overrides = columnVisibilityConfig[column.key] || {};

  return {
    ...column,
    visible: overrides.visible ?? column.visible ?? true,
    hideIfEmpty: overrides.hideIfEmpty ?? column.hideIfEmpty ?? false,
  };
};

const getVisibleColumns = (rows) => {
  const columnsWithConfig = BASE_COLUMNS.map((column) => applyVisibilityConfig(column));

  return columnsWithConfig.filter((column) => {
    if (!column.visible) return false;

    if (column.hideIfEmpty && rows?.length > 0) {
      return rows.some((row) => hasValue(getColumnValue(column, row)));
    }

    return true;
  });
};

const TicketTable = ({ data = [] }) => {
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Load sort config from localStorage
  useEffect(() => {
    const storedSort = localStorage.getItem(STORAGE_KEY);
    if (storedSort) {
      try {
        const parsed = JSON.parse(storedSort);
        if (parsed?.key && parsed?.direction) {
          setSortConfig(parsed);
        }
      } catch (e) {
        console.error("Invalid stored sort config:", e);
      }
    }
  }, []);

  const visibleColumns = useMemo(() => getVisibleColumns(data), [data]);

  useEffect(() => {
    if (sortConfig.key && !visibleColumns.some((column) => column.key === sortConfig.key && column.sortable)) {
      setSortConfig({ key: null, direction: "asc" });
    }
  }, [visibleColumns, sortConfig.key]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const sortedData = useMemo(() => {
    if (!sortConfig.key) return data;

    const sortColumn = visibleColumns.find((column) => column.key === sortConfig.key);
    if (!sortColumn || !sortColumn.sortable) return data;
    const getSortValue = sortColumn.sortValueGetter || sortColumn.valueGetter || ((row) => row?.[sortColumn.key]);

    return [...data].sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, visibleColumns]);

  const currentData = sortedData.slice(indexOfFirstItem, indexOfLastItem);

  // Pagination from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const page = params.get("page");

    if (page) {
      setCurrentPage(parseInt(page, 10));
    } else {
      const newUrl = `${location.pathname}?page=1`;
      window.history.pushState({}, "", newUrl);
    }
  }, [location.search]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const newDirection = prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      const newSort = { key, direction: newDirection };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newSort));
      return newSort;
    });
  };

  return (
    <>
      <div className="p-6 overflow-scroll px-0">
        <table className="mt-4 w-full min-w-max table-auto text-left resizable-table">
          <TicketTableHeader columns={visibleColumns} onSort={handleSort} sortConfig={sortConfig} />
          <tbody>
            {currentData.length ? (
              currentData.map((row) => <TicketTableRow key={row.id} row={row} columns={visibleColumns} />)
            ) : (
              <tr>
                <td className="p-2" colSpan={Math.max(visibleColumns.length, 1)}>
                  <p className="w-full mt-10 mb-10 py-10 bg-background-3 h-50 flex items-center justify-center rounded-md">
                    No tickets waiting your attention
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <TicketTablePagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        indexOfFirstItem={indexOfFirstItem}
        indexOfLastItem={indexOfLastItem}
        totalItems={data.length}
      />
    </>
  );
};

export default TicketTable;
